// Heals legacy markdown produced before the export-side fix in
// boundaryFormatLeak.ts. When a text-format span (bold, italic, etc.) crossed
// a `[size=N]…[/size]` boundary, Lexical's exporter emitted unbalanced
// markers that fail to round-trip on import. Two shapes were produced:
//
//   Right leak:  `[size=N]**X[/size]Y**`           (opening inside, closing outside)
//   Left leak:   `**X[size=N]Y[/size]…`            (opening outside, no closing)
//
// On a subsequent round-trip the leaked markers were lost from the model and
// re-emitted as escaped text content (Lexical's exportTextFormat escapes
// `*`/`_`/`` ` ``/`~` in literal text), producing the same shapes with
// `\*\*` in place of `**`. The heal recognises both forms.
//
// We rewrite to balanced form before handing the markdown to Lexical:
//
//   `[size=N]**X[/size]Y**`     →  `[size=N]**X**[/size]**Y**`
//   `[size=N]\*\*X[/size]Y\*\*` →  `[size=N]**X**[/size]**Y**`
//   `**X[size=N]Y[/size]Z`      →  `**X[size=N]Y[/size]Z**`           (extend to EOL)
//   `\*\*X[size=N]Y[/size]Z`    →  `**X[size=N]Y[/size]Z**`
//   `**X[size=N]Y[/size]`       →  `**X**[size=N]**Y**[/size]`         (close at [/size])
//
// Already-balanced markdown (e.g. `**X[size=N]Y[/size]Z**`, where the bold
// span fully wraps the size and Lexical's outermost-match logic handles the
// import correctly) is left untouched by the negative-lookahead guard.

interface MarkerSpec {
  /** The literal markdown marker we emit when rewriting (always unescaped). */
  marker: string;
  /**
   * Anchored marker pattern: matches the marker either unescaped (`**`) or
   * Lexical-escaped (`\*\*`). Includes lookbehind/lookahead anchors so we
   * don't match a marker that's part of a longer one (e.g. `**` inside
   * `***`). The escaped variant doesn't need those anchors because the `\`
   * shields it from concatenation with neighbouring markers.
   */
  anchored: string;
  /** Bare marker pattern (escaped or unescaped) for use inside lookaheads. */
  bare: string;
  /** Content that does NOT contain the bare marker (or a size tag). */
  notMarker: string;
}

// Content excluded from X/Y captures: any size tag — we never want to span
// across a `[size=…]` or `[/size]` boundary, which would conflate independent
// size blocks (e.g. fragmented `[size=N]**1**[/size][size=N])…[/size]` from
// PR #10522, where the inner `**` is balanced and not a leak).
const SIZE_TAG_EXCLUDE = String.raw`\[size=|\[\/size\]`;

// Trailing content that's "safe" to wrap inside an extended-to-EOL marker:
// no format markers (whose presence makes wrapping ambiguous or invalid)
// AND no further size-tag boundaries (otherwise we'd extend the marker
// across an unrelated `[size=N]…[/size]` block, e.g.
// `**X[size=18]Y[/size][size=24]Z[/size]` would wrongly bold the `Z`).
const SAFE_TRAILING =
  String.raw`(?:(?!${SIZE_TAG_EXCLUDE})[^*_~\\\n` + '`' + '])*';

const BOLD: MarkerSpec = {
  marker: '**',
  // `(?<![\\*])\*\*(?!\*)` — true `**` not adjacent to `\` or `*`.
  // `\\\*\\\*`             — Lexical-escaped literal `**` (i.e. `\*\*`).
  anchored: String.raw`(?:(?<![\\*])\*\*(?!\*)|\\\*\\\*)`,
  bare: String.raw`(?:\*\*|\\\*\\\*)`,
  notMarker: String.raw`(?:(?!\*\*|\\\*\\\*|${SIZE_TAG_EXCLUDE}).)*?`,
};

const STRIKETHROUGH: MarkerSpec = {
  marker: '~~',
  anchored: String.raw`(?:(?<!\\)~~(?!~)|\\~\\~)`,
  bare: String.raw`(?:~~|\\~\\~)`,
  notMarker: String.raw`(?:(?!~~|\\~\\~|${SIZE_TAG_EXCLUDE}).)*?`,
};

const CODE: MarkerSpec = {
  marker: '`',
  anchored: '(?:(?<![\\\\`])`(?!`)|\\\\`)',
  bare: '(?:`|\\\\`)',
  notMarker: '(?:(?!`|\\\\`|' + SIZE_TAG_EXCLUDE + ').)*?',
};

const ITALIC: MarkerSpec = {
  marker: '*',
  // True italic `*` (not adjacent to `\` or `*`) OR Lexical-escaped `\*`
  // (also requiring no following escaped `*` so we don't match half of
  // `\*\*`). Italic's bare must distinguish a true italic `*` from one
  // inside `**` — otherwise the left-leak negative lookahead concludes a
  // leak is balanced when the only `*` later is inside `**bold**`.
  anchored: String.raw`(?:(?<![\\*])\*(?!\*)|\\\*(?!\\\*))`,
  bare: String.raw`(?:(?<![\\*])\*(?!\*)|\\\*(?!\\\*))`,
  notMarker: String.raw`(?:(?!\*|\\\*|${SIZE_TAG_EXCLUDE}).)*?`,
};

// Process longer/more-specific markers before shorter ones to avoid
// accidental cross-format matches (`*` matching half of `**`, etc.).
const MARKERS: ReadonlyArray<MarkerSpec> = [BOLD, STRIKETHROUGH, CODE, ITALIC];

function healLineForMarker(line: string, spec: MarkerSpec): string {
  const { marker, anchored, bare, notMarker } = spec;

  // Pass 1: right leak — both opener and closer present, just unbalanced
  // around `[/size]`. Rewrite to balance markers inside the size block and
  // around the trailing run.
  //   `[size=N]<m>X[/size]Y<m>`  →  `[size=N]<m>X<m>[/size]<m>Y<m>`
  const rightLeak = new RegExp(
    `\\[size=(\\d+)\\]${anchored}(${notMarker})\\[\\/size\\](${notMarker})${anchored}`,
    'g'
  );
  let result = line.replace(
    rightLeak,
    (_full, size: string, x: string, y: string) =>
      `[size=${size}]${marker}${x}${marker}[/size]${marker}${y}${marker}`
  );

  // Pass 2: left leak with safe trailing — opener present, no closer
  // anywhere later, and the text after `[/size]` contains no other format
  // markers. Extend the marker to wrap everything up to end-of-line so
  // Lexical parses it as a single Case-C span (which round-trips cleanly
  // because BOLD_STAR / etc. wraps FONT_SIZE in the outermost-match check).
  //   `<m>X[size=N]Y[/size]Z`  →  `<m>X[size=N]Y[/size]Z<m>`
  const leftLeakExtend = new RegExp(
    `${anchored}(${notMarker})\\[size=(\\d+)\\](${notMarker})\\[\\/size\\](${SAFE_TRAILING})$`,
    'g'
  );
  result = result.replace(
    leftLeakExtend,
    (_full, x: string, size: string, y: string, trailing: string) =>
      `${marker}${x}[size=${size}]${y}[/size]${trailing}${marker}`
  );

  // Pass 3: left leak with unsafe trailing — opener present, no closer
  // later, but the trailing run contains other format markers (so we
  // can't safely wrap it). Close the marker at `[/size]` and balance
  // around the inner content.
  //   `<m>X[size=N]Y[/size]<unsafe>` → `<m>X<m>[size=N]<m>Y<m>[/size]<unsafe>`
  // The negative lookahead asserts no closer exists later on the line.
  const leftLeakClose = new RegExp(
    `${anchored}(${notMarker})\\[size=(\\d+)\\](${notMarker})\\[\\/size\\](?!(?:(?!${bare}).)*${bare})`,
    'g'
  );
  result = result.replace(
    leftLeakClose,
    (_full, x: string, size: string, y: string) =>
      `${marker}${x}${marker}[size=${size}]${marker}${y}${marker}[/size]`
  );

  return result;
}

/**
 * Rewrites broken `[size=N]…[/size]` blocks where text-format markers leak
 * across the boundary. Processes line-by-line because `[size=N]…[/size]`
 * cannot span newlines (the FONT_SIZE_IMPORT_REGEXP non-greedy `.+?` does
 * not cross `\n`). No-op for already-balanced markdown.
 */
export function healFontSizeFormatLeak(markdown: string): string {
  if (!markdown.includes('[size=') || !markdown.includes('[/size]')) {
    return markdown;
  }

  const lines = markdown.split('\n');
  const fixed = lines.map((line) => {
    if (!line.includes('[size=') || !line.includes('[/size]')) return line;
    let result = line;
    for (const spec of MARKERS) {
      result = healLineForMarker(result, spec);
    }
    return result;
  });

  return fixed.join('\n');
}
